import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { Overlay } from '@argo/base-patterns'
import { ToggleRadio, Heading, Box } from '@argo/ui-elements'
import { FlexBox } from '@argo/principles'
import styled from '@emotion/styled'
import useSendRequestAppointment from '../../hooks/useSendRequestAppointment'
import BasicButton from '../generic/button'
import { kbbTheme } from '@argo/themes'
import { ThemeProvider } from '@emotion/react'
import { breakpoints } from '../../constants'
import { useEdgeGetRequestAppointmentSettings } from '../../hooks/useEdgeDataFetcher'
import { greenBase } from '../../constants/principles'

const Header = ({ headingText, subHeadingText, subHeading, green }) => {
  return (
    <HeaderStyled green={green}>
      <Heading headingName="SubsectionHeadingExtra">{headingText}</Heading>
      {subHeadingText && <Heading headingName="ContentHeadingSemi">{subHeadingText}</Heading>}
      {subHeading}
    </HeaderStyled>
  )
}

function mapAppointSettings(apptSettings) {
  if (!apptSettings) return {}
  const days = apptSettings.split(',')
  const settings = {}
  days.forEach(day => {
    const [date, start, end] = day.split('-')
    const weekday = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 }
    settings[weekday[date]] = {
      start: { h: parseInt(start.slice(0, 2)), m: parseInt(start.slice(2, 4)) },
      end: { h: parseInt(end.slice(0, 2)), m: parseInt(end.slice(2, 4)) }
    }
  })
  return settings
}

function timeRangeLabelsBuilder({ start, end }, daySelected) {
  if (!start || !end) return []
  let runner = moment(`${daySelected.format('DD MMMM YYYY')} ${start.h}:${start.m}`, 'DD MMMM YYYY HH:mm')
  const timeRanges = []
  while (true) {
    timeRanges.push({ label: runner.format('hh:mm A'), moment: runner, h: runner.get('hours'), m: runner.get('minutes') })
    runner = runner.add(30, 'minutes')
    if (runner > moment(`${daySelected.format('DD MMMM YYYY')} ${end.h}:${end.m}`, 'DD MMMM YYYY HH:mm') || timeRanges.length > 48) break
  }
  const result = []
  if (start.h < 12) {
    result.push({
      label: 'AM',
      times: timeRanges.filter(item => item.h < 12)
    })
  }
  if (end.h >= 12) {
    result.push({
      label: 'PM',
      times: timeRanges.filter(item => item.h >= 12)
    })
  }
  return result
}

const Content = ({ setRequestApp, dealer, setAppointmentDate, offerId, updateGlobalState }) => {
  const [isDaySelected, setIsDaySelected] = useState(false)
  const [isTimeSlotSelected, setIsTimeSlotSelected] = useState(false)
  const [isAppointmentRequested, setIsAppointmentRequested] = useState(false)
  const [daySelected, setDaySelected] = useState('')
  const [timeSelected, setTimeSelected] = useState('')
  const [appointmentSettingsFromEdge] = useEdgeGetRequestAppointmentSettings(dealer.dealerId)
  const appointmentSettings = mapAppointSettings(appointmentSettingsFromEdge ? appointmentSettingsFromEdge.settings : undefined)

  useEffect(() => {
    if (appointmentSettingsFromEdge && appointmentSettingsFromEdge.settings) {
      console.log('appointmentSettingsFromEdge.settings', appointmentSettingsFromEdge.settings)
      const weekdayStrings = appointmentSettingsFromEdge.settings.split(',').map(item => {
        const [weekdayString] = item.split('-')
        return weekdayString
      })
      for (var idx = 0; idx < 9; idx++) {
        const day = moment().add(idx, 'days')
        if (weekdayStrings.includes(day.format('dddd'))) {
          setDaySelected(day.format('DD MMMM YYYY'))
          setIsDaySelected(true)
          break
        }
      }
    }
  }, [appointmentSettingsFromEdge])
  const [isSendingRequestAppoiment, isSentRequestAppointment, sendRequestAppointment] = useSendRequestAppointment()

  const submitAppointmentRequest = () => {
    const SdSessionId = getSdSessionId()
    sendRequestAppointment({
      DealerId: dealer.dealerId,
      OfferId: offerId,
      DayOfWeek: daySelected.trim(),
      TimeFrame: timeSelected,
      SdSessionId
    })

    const getTimeTag = timeSelected.includes('Morning') ? 'Morn' : timeSelected.includes('Afternoon') ? 'Noon' : 'Eve'
    updateGlobalState({
      fireAnalytics: `${daySelected.trim().slice(0, 3)}_${getTimeTag}_SendAppt`
    })
  }

  useEffect(() => {
    if (!isSendingRequestAppoiment && isSentRequestAppointment) {
      setIsAppointmentRequested(true)
      setAppointmentDate(`${daySelected} ${timeSelected}`)
      setRequestApp(true)
    }
  }, [isSentRequestAppointment])

  let dateStartOfWeek = moment().startOf('week')
  let dates = Array(14)
    .fill(0)
    .map((_, idx) => moment(dateStartOfWeek).add(idx, 'days'))

  const getSdSessionId = () => {
    try {
      if (!window.ICO_isSDLoaded) return undefined
      const { isStellantisDealer, isToyotaDealer, oemDealerCode, isLexusDealer, isGMDealer, isGenesisDealer } = window.ICO.dataLayer.offer
      if (oemDealerCode && (isStellantisDealer || isToyotaDealer || isLexusDealer || isGMDealer || isGenesisDealer)) return window.sd.SessionId
      return undefined
    } catch {
      return undefined
    }
  }

  const dealershipName = dealer && dealer.dealershipName ? dealer.dealershipName : 'The Dealer'
  const datetime = moment(`${daySelected} ${timeSelected}`, 'DD MMMM YYYY hh:mm A').format('dddd, MMMM DD [at] hh:mma')
  return isAppointmentRequested ? (
    <StyledOverlayContainer>
      <Header
        green
        headingText="Appointment Request Sent"
        subHeading={
          <Box>
            <FlexBox alignItems="center" flexWrap="wrap" horizontalSpacing="micro" verticalSpacing="sm" justifyContent="center">
              <Heading data-testid={`oc-txt-appointment-reqests-confirmed-datetime`} headingName="ContentHeadingSemi">
                {datetime}
              </Heading>
            </FlexBox>
          </Box>
        }
      />
      <StyledConfirmTextContainer>
        <Heading headingName="SubsectionHeadingExtra">{dealershipName}</Heading>
        <Heading headingName="ContentHeadingSemi" enableTitleCase={false}>
          will reach out to you shortly to confirm your requested appointment.
        </Heading>
      </StyledConfirmTextContainer>
    </StyledOverlayContainer>
  ) : (
    <StyledOverlayContainer>
      <Header headingText="Request an Appointment" subHeadingText="Your Offer is Good for 7 Days" />
      <Heading headingName="ContentHeadingExtra">{daySelected && moment(daySelected, 'DD MMMM YYYY').format('MMMM YYYY')}</Heading>
      <GroupStyled>
        <FlexBox alignItems="center" horizontalSpacing="xs" verticalSpacing="xs" justifyContent="center">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(dayOfWeek => (
            <DayOfWeek key={dayOfWeek}>{dayOfWeek}</DayOfWeek>
          ))}
        </FlexBox>
        {[dates.slice(0, 7), dates.slice(7, 14)].map((row, rowIdx) => (
          <FlexBox alignItems="center" horizontalSpacing="xs" verticalSpacing="xs" justifyContent="center" key={rowIdx}>
            {row.map((day, index) => {
              const isDisable =
                day.endOf('day') < moment() ||
                day >
                  moment()
                    .add(7, 'day')
                    .endOf('day') ||
                !Object.keys(appointmentSettings).includes((index % 7) + '')
              const isSelected = day.get('dates') === moment(daySelected, 'DD MMMM YYYY ').get('dates')
              return (
                <DateSelectItem
                  key={index}
                  className={[isSelected ? 'selected' : '', isDisable ? 'disabled' : ''].join(' ')}
                  onClick={() => {
                    if (isDisable) return
                    setIsDaySelected(true)
                    setDaySelected(`${day.format('DD MMMM YYYY')} `)
                    setIsTimeSlotSelected(false)
                    setTimeSelected('')
                  }}>
                  {day.format('DD')}
                </DateSelectItem>
              )
            })}
          </FlexBox>
        ))}
      </GroupStyled>

      {isDaySelected && (
        <GroupStyled>
          <GroupTimesRange key={daySelected}>
            {timeRangeLabelsBuilder(daySelected && appointmentSettings[moment(daySelected, 'DD MMMM YYYY').weekday()], moment(daySelected, 'DD MMMM YYYY')).map(ampm => (
              <>
                <TimesHeading>{ampm.label}</TimesHeading>
                <Grid4>
                  {ampm.times.map(time => (
                    <ToggleRadioTimeRangeStyled
                      key={time.label}
                      captionText=""
                      labelConfig={{
                        label: time.label,
                        textAlign: 'center'
                      }}
                      onClick={() => {
                        if (isDaySelected) {
                          setIsTimeSlotSelected(true)
                          setTimeSelected(time.label)
                        }
                      }}
                      radioConfig={{
                        disabled: !isDaySelected,
                        id: `morning`,
                        name: `radio-time`,
                        padding: ''
                      }}
                      supplementalText=""
                      isShowRadio={false}
                    />
                  ))}
                </Grid4>
              </>
            ))}
          </GroupTimesRange>
        </GroupStyled>
      )}
      <SendButton
        data-testid={`oc-btn-send-appointment-request`}
        buttonType="primary"
        inverse="false"
        text={isSendingRequestAppoiment ? 'Sending' : 'Request Appointment'}
        disabled={isSendingRequestAppoiment || !isDaySelected || !isTimeSlotSelected}
        href="#"
        target="_self"
        rel="nofollow"
        enableCapitalize={true}
        sizeType="regular"
        onClick={() => {
          submitAppointmentRequest()
          updateGlobalState({ fireAnalytics: `submit_request_appointment` })
        }}>
        {isSendingRequestAppoiment ? 'Sending' : 'Request Appointment'}
      </SendButton>
    </StyledOverlayContainer>
  )
}

export default ({ showing, setShowing, dealer, setDealer, setAppointmentDate, offerId, updateGlobalState }) => {
  const [requestedApp, setRequestApp] = useState(false)

  return (
    <ThemeProvider theme={kbbTheme}>
      <Overlay
        showOverlay={showing}
        widthRatio={90}
        maxHeightRatio={90}
        maxWidth={'520px'}
        closeOnClickOutside={false}
        onClose={() => {
          setShowing(false)
          if (dealer) {
            setDealer(undefined)
          }
        }}>
        {<Content setRequestApp={setRequestApp} dealer={dealer} setAppointmentDate={setAppointmentDate} offerId={offerId} updateGlobalState={updateGlobalState} />}
      </Overlay>
    </ThemeProvider>
  )
}

const StyledOverlayContainer = styled.div`
  max-height: 660px;
  align-content: center;
  h1 {
    text-align: center;
  }
`

const ToggleRadioStyled = styled(ToggleRadio)`
  .toggle {
    height: auto !important;
    padding: 12px 16px 10px 16px;
  }
  .custom-radio:disabled ~ .overlay div {
    font-weight: 400;
  }
  .custom-radio: checked ~.overlay > .toggle {
    background: #0057b5;
    border: 1px solid #0057b5;
    div {
      color: #fff;
    }
  }
`

const ToggleRadioTimeRangeStyled = styled(ToggleRadioStyled)`
  .toggle {
    white-space: pre-line;
    padding: 5px;
  }
`

const StyledConfirmTextContainer = styled.div`
  padding: 20px 0px 0px 0px;
  text-align: center;
`

const HeaderStyled = styled.div`
  text-align: center;
  margin-bottom: 20px;
  ${p =>
    p.green &&
    ` h1 {
      color: ${greenBase};
    }`}
`

const GroupStyled = styled.div`
  padding: 20px 0;
`

const SendButton = styled(BasicButton)`
  width: 100%;
`

const DayOfWeek = styled.div`
  text-align: center;
  width: 40px;
  @media (min-width: ${breakpoints.mid}px) {
    width: 50px;
  }
`
const Grid4 = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, 1fr);
`

const GroupTimesRange = styled.div`
  border: 1px solid #ddd;
  padding: 15px 15px 10px;
`

const TimesHeading = styled.div`
  color: #ccc;
  font-weight: 600;
`

const DateSelectItem = styled.div`
  border-radius: 30px;
  text-align: center;
  padding: 5px;
  height: 40px;
  width: 40px;
  border: 1px solid #aaa;
  cursor: pointer;
  font-weight: 600;
  @media (min-width: ${breakpoints.mid}px) {
    padding: 10px;
    width: 48px;
    height: 48px;
    margin: 5px;
  }
  &.disabled {
    border-color: #ebebeb;
    font-color: #dcdcdc;
    cursor: not-allowed;
    font-weight: 400;
  }
  &.selected {
    border: 1px solid #0057b5;
    color: #fff;
    background: #0057b5;
  }
`
